import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useFaq from '@/api/useFaq.ts';
import useRestaurants from '@/api/useRestaurants.ts';
import useFaqAdd from '@/api/useFaqAdd.ts';
import { Schema, schema } from '@/components/modals/Faq/AddFaqModal.tsx';

type CopyFaqModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  questionId: number | null;
};
function CopyFaqModal({ isOpen, onClose, onConfirm, questionId }: CopyFaqModalProps) {
  const { t } = useTranslation();
  const { data: restaurants, isLoading: isLoadingRestaurants } = useRestaurants();
  const {
    mutateAsync: addFaq,
    isError: isErrorAddFaq,
    isPending: isLoadingAddFaq,
    isSuccess: isSuccessAddFaq,
    error: errorAddFaq,
  } = useFaqAdd();

  const {
    data: currentData,
    isLoading: isLoadingCurrentData,
    isSuccess,
  } = useFaq(!!questionId, {
    question_id: questionId,
  });

  const { handleSubmit, control, setValue } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      restaurant_id: '',
      question: '',
      answer: '',
    },
  });

  const handleAddFaq = ({ restaurant_id, ...rest }: Schema) => {
    void (async () => {
      try {
        if (isLoadingAddFaq || isLoadingRestaurants || !currentData || !currentData[0]) {
          return;
        }

        await addFaq({
          ...rest,
          restaurant_id: parseInt(restaurant_id),
        });

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error adding faq: ', error);
      }
    })();
  };

  useEffect(() => {
    if (isSuccess && currentData[0]) {
      const { question, answer } = currentData[0];
      setValue('question', question);
      setValue('answer', answer ?? '');
    }
  }, [currentData, isLoadingCurrentData, isSuccess, setValue]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleAddFaq)}>
        <DialogTitle>{t('modals.addFaqModal.title')}</DialogTitle>
        <DialogContent>
          <LoaderWrapper isLoading={isLoadingCurrentData || isLoadingRestaurants}>
            <div className="pt-2 flex flex-col gap-4">
              <div className="flex flex-col gap-2 empty:hidden">
                {isSuccessAddFaq && <Alert severity="success">{t('modals.addFaqModal.successMessage')}</Alert>}
                {isErrorAddFaq && <Alert severity="error">{errorAddFaq?.message as string}</Alert>}
              </div>
              <div className="flex flex-col gap-4 w-full">
                <Controller
                  control={control}
                  name="restaurant_id"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.restaurant_id}>
                      <InputLabel shrink id="restaurant_id">
                        {t('label.restaurant')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.restaurant')}
                        labelId="restaurant_id"
                        renderValue={(currentValue) =>
                          currentValue == '' ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.restaurant')}</span>
                          ) : (
                            restaurants?.find((restaurant) => restaurant.id === parseInt(currentValue))?.name
                          )
                        }
                      >
                        {restaurants?.map((restaurant) => (
                          <MenuItem key={`restaurant_${restaurant.id}`} value={restaurant.id}>
                            {restaurant.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.restaurant_id?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="question"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.question}
                      helperText={errors.question?.message}
                      value={value}
                      onChange={onChange}
                      multiline
                      minRows={2}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.question')}
                      placeholder={t('placeholder.question')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="answer"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.answer}
                      helperText={errors.answer?.message}
                      value={value}
                      onChange={onChange}
                      multiline
                      minRows={4}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.answer')}
                      placeholder={t('placeholder.answer')}
                    />
                  )}
                />
              </div>
            </div>
          </LoaderWrapper>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton color="primary" variant="contained" loading={isLoadingAddFaq} type="submit">
              {t('button.save')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CopyFaqModal;
